import { useStakingScreenContext } from '@app/screens/Staking/StakingScreenProvider';
import { CustomCard } from '@app/shared/components';
import { AppSelector, useAppTheme } from '@itrustcapital/ui';
import { Text } from '@ui-kitten/components';
import { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

export function StakingBalances() {
	const styles = useCustomStyles();
	const {
		iraStakingAmounts,
		pcaStakingAmounts,
		tpaStakingAmounts,
		iraUnstakingAmounts,
		pcaUnstakingAmounts,
		tpaUnstakingAmounts,
	} = useStakingScreenContext();

	const availableCurrencies = useMemo(() => {
		const allAmounts = [
			...(iraStakingAmounts ?? []),
			...(pcaStakingAmounts ?? []),
			...(tpaStakingAmounts ?? []),
			...(iraUnstakingAmounts ?? []),
			...(pcaUnstakingAmounts ?? []),
			...(tpaUnstakingAmounts ?? []),
		];

		const uniqueCurrencies = Array.from(new Set(allAmounts.map((item) => item.ticker))).filter(
			(ticker) => ticker !== 'DOT'
		);

		return uniqueCurrencies;
	}, [
		iraStakingAmounts,
		pcaStakingAmounts,
		tpaStakingAmounts,
		iraUnstakingAmounts,
		pcaUnstakingAmounts,
		tpaUnstakingAmounts,
	]);

	const [selectedCurrency, setSelectedCurrency] = useState<string>(
		availableCurrencies[0] ?? 'SOL'
	);

	const iraStaking = iraStakingAmounts?.find((x) => x.ticker === selectedCurrency);
	const iraUnstaking = iraUnstakingAmounts?.find((x) => x.ticker === selectedCurrency);
	const pcaStaking = pcaStakingAmounts?.find((x) => x.ticker === selectedCurrency);
	const pcaUnstaking = pcaUnstakingAmounts?.find((x) => x.ticker === selectedCurrency);
	const tpaStaking = tpaStakingAmounts?.find((x) => x.ticker === selectedCurrency);
	const tpaUnstaking = tpaUnstakingAmounts?.find((x) => x.ticker === selectedCurrency);

	if (!selectedCurrency) {
		return null;
	}

	return (
		<View>
			<View style={styles.filter.container}>
				<AppSelector
					closeOnSelect
					data={availableCurrencies}
					itemDisplay={(ticker: string) => ticker}
					label="Currency"
					value={selectedCurrency}
					onSelect={(value: string) => {
						setSelectedCurrency(value);
					}}
				/>
			</View>

			<View style={styles.main.cards}>
				<View style={styles.main.container}>
					<Text style={styles.main.header}>IRA Amounts</Text>
					{iraStaking && (
						<CustomCard style={styles.main.card} bodyStyle={styles.main.cardContent}>
							<View style={styles.main.cardContent}>
								<Text style={styles.main.amount}>{iraStaking.principleAmount}</Text>
								<Text style={styles.main.text}>Staking {iraStaking.ticker}</Text>
							</View>
						</CustomCard>
					)}
					{iraUnstaking && (
						<CustomCard style={styles.main.card} bodyStyle={styles.main.cardContent}>
							<View style={styles.main.cardContent}>
								<Text style={styles.main.amount}>{iraUnstaking.totalAmount}</Text>
								<Text style={styles.main.text}>
									Unstaking {iraUnstaking.ticker}
								</Text>
							</View>
						</CustomCard>
					)}
				</View>

				<View style={styles.main.container}>
					<Text style={styles.main.header}>PCA Amounts</Text>
					{pcaStaking && (
						<CustomCard style={styles.main.card} bodyStyle={styles.main.cardContent}>
							<View style={styles.main.cardContent}>
								<Text style={styles.main.amount}>{pcaStaking.principleAmount}</Text>
								<Text style={styles.main.text}>Staking {pcaStaking.ticker}</Text>
							</View>
						</CustomCard>
					)}
					{pcaUnstaking && (
						<CustomCard style={styles.main.card} bodyStyle={styles.main.cardContent}>
							<View style={styles.main.cardContent}>
								<Text style={styles.main.amount}>{pcaUnstaking.totalAmount}</Text>
								<Text style={styles.main.text}>
									Unstaking {pcaUnstaking.ticker}
								</Text>
							</View>
						</CustomCard>
					)}
				</View>

				<View style={styles.main.container}>
					<Text style={styles.main.header}>TPA Amounts</Text>
					{tpaStaking && (
						<CustomCard style={styles.main.card} bodyStyle={styles.main.cardContent}>
							<View style={styles.main.cardContent}>
								<Text style={styles.main.amount}>{tpaStaking.principleAmount}</Text>
								<Text style={styles.main.text}>Staking {tpaStaking.ticker}</Text>
							</View>
						</CustomCard>
					)}
					{tpaUnstaking && (
						<CustomCard style={styles.main.card} bodyStyle={styles.main.cardContent}>
							<View style={styles.main.cardContent}>
								<Text style={styles.main.amount}>{tpaUnstaking.totalAmount}</Text>
								<Text style={styles.main.text}>
									Unstaking {tpaUnstaking.ticker}
								</Text>
							</View>
						</CustomCard>
					)}
				</View>
			</View>
		</View>
	);
}

function useCustomStyles() {
	const theme = useAppTheme();

	return {
		filter: StyleSheet.create({
			container: {
				marginBottom: 20,
				width: 300,
				flexDirection: 'row',
			},
		}),
		main: StyleSheet.create({
			container: {
				paddingBottom: 20,
				justifyContent: 'flex-start',
				flexDirection: 'column',
				flexWrap: 'wrap',
				gap: 8,
			},
			cards: {
				gap: 8,
				flexDirection: 'row',
				justifyContent: 'flex-start',
			},
			card: {
				width: '100%',
				flexDirection: 'row',
				paddingVertical: 18,
				paddingHorizontal: 12,
				backgroundColor: theme['color-basic-1000'],
			},
			cardContent: {
				flexDirection: 'row',
				paddingTop: 0,
				paddingHorizontal: 0,
				justifyContent: 'center',
			},
			header: {
				fontWeight: 'bold',
				marginBottom: 8,
			},
			amount: { paddingTop: 0 },
			text: {
				paddingTop: 0,
				marginLeft: 8,
			},
		}),
	};
}
